import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import useActiveMagasin from '../hooks/useActiveMagasin';
import Layout from '../components/Layout';
import {
  AlertTriangle,
  ArrowLeft,
  Banknote,
  CheckCircle,
  Coins,
  Lock,
  Printer,
  RefreshCw,
  Users,
} from 'lucide-react';

const DENOMINATIONS = [100, 50, 20, 10, 5, 2, 1, 0.5, 0.2, 0.1, 0.05];

const buildDenoms = () => DENOMINATIONS.map(value => ({ value, quantity: 0 }));

const getDenominationStyle = (value) => {
  if (value >= 20) return {
    gradient: 'from-emerald-500/25 to-cyan-500/15',
    border: 'border-emerald-400/30',
    labelKey: 'closeCaisse.denomBillet',
    icon: Banknote,
  };
  if (value >= 5) return {
    gradient: 'from-blue-500/25 to-indigo-500/15',
    border: 'border-blue-400/30',
    labelKey: 'closeCaisse.denomBillet',
    icon: Banknote,
  };
  return {
    gradient: 'from-amber-500/25 to-orange-500/15',
    border: 'border-amber-400/30',
    labelKey: 'closeCaisse.denomPiece',
    icon: Coins,
  };
};

const DenominationCard = ({ item, onChange }) => {
  const { t, formatCurrency } = useLanguage();
  const style = getDenominationStyle(item.value);
  const Icon = style.icon;
  return (
    <div className={`rounded-2xl border ${style.border} bg-gradient-to-br ${style.gradient} p-4 shadow-lg shadow-black/10`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-white font-black text-lg">{formatCurrency(item.value)}</div>
            <div className="text-xs uppercase tracking-wide text-gray-300">{t(style.labelKey)}</div>
          </div>
        </div>
        <div className="text-[11px] text-gray-200 px-2 py-1 rounded-full border border-white/10 bg-white/5">
          {t('closeCaisse.countBadge', { count: item.quantity })}
        </div>
      </div>
      <div className="mt-4 flex items-end justify-between gap-3">
        <div className="text-xs text-gray-300">
          {t('closeCaisse.total')}
          <div className="text-white font-bold text-sm">{formatCurrency(item.value * item.quantity)}</div>
        </div>
        <input
          type="number"
          min="0"
          value={item.quantity}
          onChange={(e) => onChange(e.target.value)}
          className="input-dark w-24 py-2 text-center text-white font-semibold"
          data-testid={`close-denom-${item.value}`}
        />
      </div>
    </div>
  );
};

const CloseCaissePage = () => {
  const { api, isAdmin, user } = useAuth();
  const { t, tError, formatCurrency } = useLanguage();
  const { code: sessionMagasin } = useActiveMagasin();
  const navigate = useNavigate();
  const location = useLocation();
  const urlMagasin = new URLSearchParams(location.search).get('magasin_id');
  const magasinId = (urlMagasin || sessionMagasin || user?.default_magasin_id || 'M1').toUpperCase();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [closeDenoms, setCloseDenoms] = useState(buildDenoms());
  const [closeSummary, setCloseSummary] = useState(null);
  const [cbConfirmed, setCbConfirmed] = useState(false);
  const [montantCoffre, setMontantCoffre] = useState(0);
  const [closing, setClosing] = useState(false);

  const fetchCaisseSummary = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const response = await api.get('/caisse/summary', { params: { magasin_id: magasinId } });
      setCloseSummary(response.data);
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('closeCaisse.errLoad'));
      navigate('/dashboard');
    } finally {
      if (!silent) setLoading(false);
      setRefreshing(false);
    }
  }, [api, magasinId, navigate, tError, t]);

  useEffect(() => {
    fetchCaisseSummary();
  }, [fetchCaisseSummary]);

  const totalClose = useMemo(() => closeDenoms.reduce((sum, item) => sum + item.value * item.quantity, 0), [closeDenoms]);

  const updateDenoms = (index, value) => {
    const qty = Math.max(0, parseInt(value, 10) || 0);
    setCloseDenoms(prev => prev.map((item, i) => (i === index ? { ...item, quantity: qty } : item)));
  };

  const handleImprimerRapport = async () => {
    if (!closeSummary?.caisse_id) return;
    try {
      const response = await api.get(`/caisse/print/${closeSummary.caisse_id}`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('closeCaisse.errPdf'));
    }
  };

  const handleValiderCaisse = async () => {
    if (!closeSummary?.caisse_id) return;
    if (!cbConfirmed) {
      alert(t('closeCaisse.errConfirmCb'));
      return;
    }

    const ecart = Math.abs(totalClose - closeSummary.total_theorique);
    if (
      ecart > 5 &&
      !window.confirm(
        t('closeCaisse.confirmEcart', {
          ecart: formatCurrency(ecart),
          theorique: formatCurrency(closeSummary.total_theorique),
          compte: formatCurrency(totalClose),
        })
      )
    ) {
      return;
    }

    setClosing(true);
    try {
      await api.post(`/caisse/fermer/${closeSummary.caisse_id}`, {
        total: totalClose,
        details: closeDenoms,
        cb_confirmed: cbConfirmed,
        montant_coffre: montantCoffre,
      });
      alert(t('closeCaisse.closeSuccess', { magasin: magasinId }));
      navigate('/dashboard');
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('closeCaisse.errClose'));
    } finally {
      setClosing(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="loading-spinner" />
        </div>
      </Layout>
    );
  }

  const ecart = totalClose - (closeSummary?.total_theorique || 0);
  const ecartAbs = Math.abs(ecart);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto fade-in space-y-6">
        <div className="flex items-center justify-between gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-5 h-5" />
            {t('closeCaisse.backToDashboard')}
          </button>
          <div className="text-center">
            <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-pink-500">
              <Lock className="w-6 h-6 inline-block mr-2" />
              {t('closeCaisse.title', { magasin: magasinId })}
            </h2>
            {closeSummary?.date_caisse && (
              <p className="text-xs text-gray-400 mt-1">
                {t('closeCaisse.journee', { date: closeSummary.date_caisse })}
                {closeSummary.opened_by ? ` · ${t('closeCaisse.openedBy', { name: closeSummary.opened_by })}` : ''}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => fetchCaisseSummary({ silent: true })}
            disabled={refreshing}
            className="flex items-center gap-2 text-sm text-cyan-300 hover:text-white px-3 py-2 rounded-lg border border-cyan-400/30 bg-cyan-500/10 disabled:opacity-50"
            data-testid="refresh-caisse-summary"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            {t('closeCaisse.refresh')}
          </button>
        </div>

        <div className="glass p-6 rounded-2xl">
          <div className="flex flex-col lg:flex-row gap-6 lg:items-start lg:justify-between">
            <div className="space-y-4 flex-1">
              <div>
                <h3 className="text-lg font-bold text-white">{t('closeCaisse.summaryTitle')}</h3>
                <p className="text-sm text-gray-400">
                  {t('closeCaisse.activitySummary', {
                    magasin: magasinId,
                    ops: closeSummary?.nb_operations || 0,
                    pos: closeSummary?.nb_ventes_pos || 0,
                    acomptes: closeSummary?.nb_acomptes || 0,
                  })}
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 flex justify-between text-gray-300">
                  <span>{t('closeCaisse.fondOuverture')}</span>
                  <span className="font-semibold text-emerald-300">{formatCurrency(closeSummary?.montant_ouverture || 0)}</span>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 flex justify-between text-gray-300">
                  <span>{t('closeCaisse.ventesEspeces')}</span>
                  <span className="font-semibold">{formatCurrency(closeSummary?.total_ventes_especes || 0)}</span>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 flex justify-between text-gray-300">
                  <span>{t('closeCaisse.ventesCb')}</span>
                  <span className="font-semibold">{formatCurrency(closeSummary?.total_ventes_cb || 0)}</span>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 flex justify-between text-gray-300">
                  <span>{t('closeCaisse.paiementsStripe')}</span>
                  <span className="font-semibold text-blue-300">{formatCurrency(closeSummary?.total_stripe || 0)}</span>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 flex justify-between text-gray-300">
                  <span>{t('closeCaisse.acomptes')}</span>
                  <span className="font-semibold">{formatCurrency(closeSummary?.total_acomptes || 0)}</span>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 flex justify-between text-gray-300">
                  <span>{t('closeCaisse.retraits')}</span>
                  <span className="font-semibold text-red-300">{formatCurrency(-Math.abs(closeSummary?.total_retraits || 0))}</span>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-cyan-400/25 bg-cyan-500/10 px-5 py-4 min-w-[240px]">
              <div className="text-sm text-cyan-200">{t('closeCaisse.totalTheorique')}</div>
              <div className="text-3xl font-black text-white">{formatCurrency(closeSummary?.total_theorique || 0)}</div>
              <div className="text-xs text-cyan-100/70 mt-2">{t('closeCaisse.totalVentes', { montant: formatCurrency(closeSummary?.total_ventes || 0) })}</div>
            </div>
          </div>
        </div>

        {(closeSummary?.by_vendeur?.length > 0) && (
          <div className="glass p-6 rounded-2xl">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-cyan-300" />
              <h3 className="text-lg font-bold text-white">{t('closeCaisse.activiteVendeur')}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-400 border-b border-white/10">
                    <th className="py-2 pr-4">{t('closeCaisse.colVendeur')}</th>
                    <th className="py-2 pr-4 text-center">{t('closeCaisse.colOps')}</th>
                    <th className="py-2 pr-4 text-right">{t('closeCaisse.colEspeces')}</th>
                    <th className="py-2 pr-4 text-right">{t('closeCaisse.colCb')}</th>
                    <th className="py-2 text-right">{t('closeCaisse.total')}</th>
                  </tr>
                </thead>
                <tbody>
                  {closeSummary.by_vendeur.map((row) => (
                    <tr key={row.vendeur} className="border-b border-white/5 text-gray-200">
                      <td className="py-2.5 pr-4 font-medium text-white">{row.vendeur}</td>
                      <td className="py-2.5 pr-4 text-center">{row.nb_operations}</td>
                      <td className="py-2.5 pr-4 text-right text-emerald-300">{formatCurrency(row.especes)}</td>
                      <td className="py-2.5 pr-4 text-right text-blue-300">{formatCurrency(row.cb)}</td>
                      <td className="py-2.5 text-right font-semibold">{formatCurrency(row.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="glass p-6 rounded-2xl">
          <div className="flex items-center justify-between gap-4 mb-4">
            <div>
              <h3 className="text-lg font-bold text-white">{t('closeCaisse.comptageEspeces')}</h3>
              <p className="text-sm text-gray-400">{t('closeCaisse.comptageSubtitle')}</p>
            </div>
            <div className="rounded-2xl border border-emerald-400/25 bg-emerald-500/10 px-5 py-4 min-w-[220px]">
              <div className="text-sm text-emerald-200">{t('closeCaisse.totalCompte')}</div>
              <div className="text-3xl font-black text-white">{formatCurrency(totalClose)}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {closeDenoms.map((item, index) => (
              <DenominationCard
                key={item.value}
                item={item}
                onChange={(value) => updateDenoms(index, value)}
              />
            ))}
          </div>

          {totalClose > 0 && (
            <div className={`mt-6 p-4 rounded-2xl ${ecartAbs > 5 ? 'bg-red-500/10 border border-red-500/30' : ecartAbs > 0 ? 'bg-yellow-500/10 border border-yellow-500/30' : 'bg-green-500/10 border border-green-500/30'}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-300">{t('closeCaisse.ecart')}</span>
                <span className={`font-bold ${ecartAbs > 5 ? 'text-red-400' : ecartAbs > 0 ? 'text-yellow-400' : 'text-green-400'}`}>
                  {ecart > 0 ? '+' : ''}{formatCurrency(ecart)}
                </span>
              </div>
              {ecartAbs > 5 && (
                <div className="flex items-center gap-2 mt-2 text-xs text-red-300">
                  <AlertTriangle className="w-4 h-4" />
                  <span>{t('closeCaisse.ecartImportant')}</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="glass p-6 rounded-2xl">
          <h3 className="text-lg font-bold mb-4 text-white">{t('closeCaisse.infosComplementaires')}</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-300 mb-2">{t('closeCaisse.montantCoffreLabel')}</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={montantCoffre}
                onChange={(e) => setMontantCoffre(parseFloat(e.target.value) || 0)}
                className="input-dark w-full md:w-64 py-2"
                placeholder="0.00"
                data-testid="montant-coffre-input"
              />
              <p className="text-xs text-gray-500 mt-1">{t('closeCaisse.montantCoffreHint')}</p>
            </div>

            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="cb-confirmed"
                checked={cbConfirmed}
                onChange={(e) => setCbConfirmed(e.target.checked)}
                className="w-5 h-5 mt-0.5 rounded"
                data-testid="cb-confirmed-checkbox"
              />
              <label htmlFor="cb-confirmed" className="text-sm text-gray-300 cursor-pointer">
                <span className="font-semibold text-white">{t('closeCaisse.cbConfirmLabel')}</span>
                <p className="text-xs text-gray-500 mt-1">{t('closeCaisse.cbConfirmHint')}</p>
              </label>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-end">
          <button
            onClick={handleImprimerRapport}
            className="px-6 py-3 rounded-xl bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 font-bold flex items-center justify-center gap-2 transition-colors"
            data-testid="print-rapport-btn"
          >
            <Printer className="w-5 h-5" />
            {t('closeCaisse.imprimerRapport')}
          </button>

          {(isAdmin || ['commercial', 'technicien', 'user'].includes(user?.role)) && (
            <button
              onClick={handleValiderCaisse}
              disabled={closing || !cbConfirmed}
              className={`px-8 py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-colors ${
                !cbConfirmed ? 'bg-gray-600 text-gray-400 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600 text-white'
              }`}
              data-testid="validate-caisse-btn"
            >
              {closing ? t('closeCaisse.closing') : <><CheckCircle className="w-5 h-5" /> {t('closeCaisse.validerCaisse')}</>}
            </button>
          )}
        </div>

        {!isAdmin && !['commercial', 'technicien', 'user'].includes(user?.role) && (
          <div className="alert-warning-custom mt-6">
            <AlertTriangle className="w-5 h-5" />
            <span>{t('closeCaisse.onlyAdmin')}</span>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CloseCaissePage;
